import PropTypes from 'prop-types';
import { forwardRef } from 'react';
import { Link as RouterLink } from 'react-router-dom';
// @mui
import { Box, Tooltip, ListItemText } from '@mui/material';
// hooks
import useActiveLink from '../../../hooks/useActiveLink';
// locales
import { useLocales } from '../../../locales';
// auth
import RoleBasedGuard from '../../../auth/RoleBasedGuard';
// utils
import { hasOwnPage } from '../../../utils/navigation';
//
import Iconify from '../../iconify';
import { StyledItem, StyledIcon } from './styles';

// ----------------------------------------------------------------------

const NavItem = forwardRef(({ item, depth, open, active, isExternalLink, ...other }, ref) => {
  const { translate } = useLocales();

  const { title, path, icon, children, disabled, caption, roles } = item;

  const subItem = depth !== 1;

  // `active` acende o pai tambem na pagina de um filho; pai so e a pagina atual no path exato.
  const { active: isExactPath } = useActiveLink(path, false);
  const isCurrentPage = children ? isExactPath : active;

  const renderContent = (extraProps = {}) => (
    <StyledItem
      ref={ref}
      open={open}
      depth={depth}
      active={active}
      disabled={disabled}
      {...other}
      {...extraProps}
    >
      {icon && <StyledIcon>{icon}</StyledIcon>}

      <ListItemText
        primary={`${translate(title)}`}
        primaryTypographyProps={{
          noWrap: true,
          sx: {
            width: 72,
            fontSize: 10,
            lineHeight: '16px',
            textAlign: 'center',
            ...(active && {
              fontWeight: 'fontWeightMedium',
            }),
            ...(subItem && {
              fontSize: 14,
              width: 'auto',
              textAlign: 'left',
            }),
          },
        }}
      />

      {caption && (
        <Tooltip title={`${translate(caption)}`} arrow placement="right">
          <Iconify
            icon="eva:info-outline"
            width={16}
            sx={{
              top: 11,
              left: 6,
              position: 'absolute',
            }}
          />
        </Tooltip>
      )}

      {!!children && (
        <Iconify
          width={16}
          icon="eva:chevron-right-fill"
          sx={{
            top: 11,
            right: 6,
            position: 'absolute',
          }}
        />
      )}
    </StyledItem>
  );

  const renderItem = () => {
    if (isExternalLink) {
      return renderContent({ component: 'a', href: path, target: '_blank', rel: 'noopener' });
    }

    if (item.onClick) {
      return (
        <Box onClick={item.onClick} sx={{ cursor: 'pointer', display: 'flex', width: '100%' }}>
          {renderContent()}
        </Box>
      );
    }

    // Aqui o grupo abre no hover; como link, o clique navegaria para a propria pagina.
    if (children && !hasOwnPage(path)) {
      return renderContent();
    }

    return renderContent({
      component: RouterLink,
      to: path,
      'aria-current': isCurrentPage ? 'page' : undefined,
    });
  };

  return <RoleBasedGuard roles={roles}> {renderItem()} </RoleBasedGuard>;
});

NavItem.propTypes = {
  open: PropTypes.bool,
  active: PropTypes.bool,
  item: PropTypes.object,
  depth: PropTypes.number,
  isExternalLink: PropTypes.bool,
};

export default NavItem;
